// Scelta tra vetrina base e vetrina avanzata (Pro).
// Mostra quale delle due vedono i clienti e quale si sta modificando.
import React, { useState } from 'react';
import { Check, Crown, Eye, Loader2, Palette, Store } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const OPTIONS = [
  {
    id: 'base',
    icon: Store,
    title: 'Vetrina base',
    text: 'Il modello Rarifex: nome, descrizione, storia, foto e social.',
  },
  {
    id: 'advanced',
    icon: Palette,
    title: 'Vetrina avanzata',
    text: 'Tutto quello della base, più logo, colori, modello grafico, menu e prodotti in evidenza.',
    pro: true,
  },
];

export default function StorefrontModeSwitch({ mode, liveMode, accessReason, onSelect, busy }) {
  const [confirmBase, setConfirmBase] = useState(false);
  const choose = (id) => {
    // Tornare alla base quando l'avanzata è visibile ai clienti: chiedi conferma
    if (id === 'base' && liveMode === 'advanced' && mode !== 'base') {
      setConfirmBase(true);
      return;
    }
    onSelect(id);
  };
  return (
    <Card className="p-4 md:p-5 mb-6" data-testid="storefront-mode-switch">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-bold text-slate-800">Quale vetrina vuoi usare?</h3>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="Tipo di vetrina">
        {OPTIONS.map((o) => {
          const selected = mode === o.id;
          const live = liveMode === o.id;
          const Icon = o.icon;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={busy}
              onClick={() => choose(o.id)}
              className={`relative text-left rounded-xl border-2 p-4 transition-all disabled:opacity-60 ${
                selected ? 'border-[#008080] bg-[#008080]/5 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              data-testid={`storefront-mode-${o.id}`}
            >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-[#008080] bg-[#008080]' : 'border-slate-300'}`}>
                  {selected && <Check className="w-3 h-3 text-white" />}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Icon className="w-4 h-4 text-[#008080]" />
                    <span className="font-semibold text-slate-800">{o.title}</span>
                    {o.pro && (
                      accessReason === 'launch'
                        ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Gratis durante il lancio</Badge>
                        : <Badge className="bg-[#DAA520] text-white text-[10px]"><Crown className="w-3 h-3 mr-1" />PRO</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{o.text}</p>
                  {live && (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-emerald-700" data-testid={`storefront-live-${o.id}`}>
                      <Eye className="w-3.5 h-3.5" /> Visibile ai clienti
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {mode === 'advanced' && liveMode === 'base' && (
        <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="storefront-mode-pending">
          Stai preparando la vetrina avanzata: i clienti vedono ancora quella base finché non premi «Salva e pubblica».
        </p>
      )}

      <AlertDialog open={confirmBase} onOpenChange={setConfirmBase}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tornare alla vetrina base?</AlertDialogTitle>
            <AlertDialogDescription>
              I clienti vedranno di nuovo il modello standard di Rarifex. Logo, colori, menu e prodotti in evidenza
              restano salvati: puoi riattivare la vetrina avanzata quando vuoi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => onSelect('base')} data-testid="storefront-confirm-base">
              Usa la vetrina base
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
